"use client";

import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/common/utils/cn";
import { useTheme, type Theme } from "./ThemeProvider";

interface ThemeToggleProps {
  /**
   * Visual style variant.
   *  - `icon`  : single sun/moon button that flips between light <-> dark.
   *  - `pill`  : segmented light / system / dark control.
   */
  variant?: "icon" | "pill";
  className?: string;
}

const ORDER: Theme[] = ["light", "system", "dark"];

export function ThemeToggle({ variant = "icon", className }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();

  // Avoid SSR/initial-hydration flicker — render a placeholder until
  // mount, since the resolved theme is only known on the client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (variant === "pill") {
    return (
      <div
        role="radiogroup"
        aria-label="Theme"
        className={cn(
          "inline-flex items-center gap-0.5 rounded-full border border-border bg-muted p-0.5 text-foreground",
          className,
        )}
      >
        {ORDER.map((value) => {
          const Icon =
            value === "light" ? Sun : value === "dark" ? Moon : Monitor;
          const active = mounted && theme === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`Use ${value} theme`}
              onClick={() => setTheme(value)}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                active
                  ? "bg-background text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="size-4" />
            </button>
          );
        })}
      </div>
    );
  }

  // icon variant — single button, flips between dark <-> light explicitly.
  const handleClick = () => {
    const next: Theme = resolvedTheme === "dark" ? "light" : "dark";
    setTheme(next);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={
        mounted
          ? `Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`
          : "Toggle theme"
      }
      title="Toggle theme"
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-muted",
        className,
      )}
    >
      {mounted ? (
        resolvedTheme === "dark" ? (
          <Sun className="size-4" />
        ) : (
          <Moon className="size-4" />
        )
      ) : (
        // Static placeholder during SSR / pre-mount — keeps layout stable.
        <Sun className="size-4 opacity-0" />
      )}
    </button>
  );
}
